import { useEffect, useRef, useState, useCallback } from "react";

import { MATIC_360 } from "./matic360Content";

/**
 * "cover" fills the canvas and crops; "contain" shows the whole frame;
 * "stand" shows the whole robot, trimmed of the empty band under it, its
 * lowest point on the canvas's bottom edge, where the hint would cover it.
 */
export type FrameFit = "cover" | "contain" | "stand";

/**
 * How far down each frame the robot reaches at its lowest, shadow included,
 * across the whole set (617 of 800 rows, frame 53); "stand" draws only the
 * rows above. Measured off the alpha of the renders in frames.ts, and
 * re-measured whenever they change.
 */
const ROBOT_FOOT = 617 / 800;

interface Use360ViewerOptions {
  frames: string[];
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  enabled: boolean;
  /**
   * Pixels of drag per frame. The default turns the 60-frame set once per
   * ~360px of drag, as the old 24-frame set did at 16px.
   */
  sensitivity?: number;
  /** Draws "DRAG TO ROTATE" along the bottom edge. */
  hint?: boolean;
  fit?: FrameFit;
}

interface Use360ViewerReturn {
  /** The frame showing, updated on each key press and when a drag ends. */
  frame: number;
  loadProgress: number;
  isLoaded: boolean;
  isDragging: boolean;
  canvasReady: boolean;
}

// Frames an arrow key turns: a 24th of a turn, to the nearest whole frame
// (3 of 60, 18 degrees), near the 15 a press the WebGL viewer turned.
const keyStep = (frameCount: number) =>
  Math.max(1, Math.round(frameCount / 24));

function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

export function use360Viewer({
  frames,
  canvasRef,
  enabled,
  sensitivity = 6,
  hint = true,
  fit = "cover",
}: Use360ViewerOptions): Use360ViewerReturn {
  const imagesRef = useRef<HTMLImageElement[]>([]);
  const currentFrameRef = useRef(0);
  const dragStartXRef = useRef(0);
  const dragStartFrameRef = useRef(0);

  const [frame, setFrame] = useState(0);
  const [loadProgress, setLoadProgress] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [canvasReady, setCanvasReady] = useState(false);
  const canvasReadyRef = useRef(false);

  useEffect(() => {
    if (enabled) return;

    canvasReadyRef.current = false;
    setCanvasReady(false);
    setIsDragging(false);
  }, [enabled]);

  const drawFrame = useCallback(
    (index: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const img = imagesRef.current[index];
      if (!img) return;

      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      const w = rect.width * dpr;
      const h = rect.height * dpr;

      if (w === 0 || h === 0) return;

      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }

      ctx.clearRect(0, 0, w, h);

      if (fit === "stand") {
        const sh = img.naturalHeight * ROBOT_FOOT;
        const scale = Math.min(w / img.naturalWidth, h / sh);
        const dw = img.naturalWidth * scale;
        const dh = sh * scale;
        ctx.drawImage(
          img,
          0,
          0,
          img.naturalWidth,
          sh,
          (w - dw) / 2,
          h - dh,
          dw,
          dh,
        );
      } else {
        const scaleToFit = fit === "cover" ? Math.max : Math.min;
        const scale = scaleToFit(w / img.naturalWidth, h / img.naturalHeight);
        const dw = img.naturalWidth * scale;
        const dh = img.naturalHeight * scale;
        ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
      }

      if (hint) {
        // Rendered in-canvas because the aspect-ratio plugin positions all
        // sibling elements at top:0 and CSS overrides get swallowed.
        const fontSize = Math.round(16 * dpr);
        ctx.font = `500 ${fontSize}px "Trim Mono", monospace`;
        ctx.letterSpacing = `${-0.48 * dpr}px`;
        ctx.fillStyle = "#000";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(MATIC_360.hint, w / 2, h - 18 * dpr);
      }

      if (!canvasReadyRef.current) {
        canvasReadyRef.current = true;
        setCanvasReady(true);
      }
    },
    [canvasRef, fit, hint],
  );

  // Preload images
  useEffect(() => {
    if (
      !enabled ||
      frames.length === 0 ||
      imagesRef.current.length === frames.length
    ) {
      return;
    }

    let cancelled = false;
    const images: HTMLImageElement[] = [];
    let loaded = 0;

    const onLoad = () => {
      loaded++;
      if (cancelled) return;
      setLoadProgress(loaded / frames.length);

      if (loaded === frames.length) {
        imagesRef.current = images;
        currentFrameRef.current = 0;
        setIsLoaded(true);
      }
    };

    frames.forEach((src, i) => {
      const img = new Image();
      images[i] = img;
      img.src = src;
      img
        .decode()
        .then(onLoad)
        .catch((err) => {
          console.error(`Failed to decode frame ${i}:`, err);
          onLoad();
        });
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, frames, drawFrame]);

  // Pointer drag + keyboard + resize
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!enabled || !canvas || !isLoaded) return;

    const frameCount = frames.length;

    const onPointerDown = (e: PointerEvent) => {
      canvas.setPointerCapture(e.pointerId);
      dragStartXRef.current = e.clientX;
      dragStartFrameRef.current = currentFrameRef.current;
      setIsDragging(true);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!canvas.hasPointerCapture(e.pointerId)) return;
      const deltaX = e.clientX - dragStartXRef.current;
      const frameDelta = Math.round(deltaX / sensitivity);
      const newFrame = mod(dragStartFrameRef.current + frameDelta, frameCount);
      if (newFrame !== currentFrameRef.current) {
        currentFrameRef.current = newFrame;
        drawFrame(newFrame);
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      if (canvas.hasPointerCapture(e.pointerId)) {
        canvas.releasePointerCapture(e.pointerId);
      }
      setIsDragging(false);
      setFrame(currentFrameRef.current);
    };

    // The slider keys: arrows step either way (wrapping round), Home and
    // End go to the first and last frame.
    const step = keyStep(frameCount);
    const keyTargets: Record<string, (current: number) => number> = {
      ArrowRight: (current) => mod(current + step, frameCount),
      ArrowUp: (current) => mod(current + step, frameCount),
      ArrowLeft: (current) => mod(current - step, frameCount),
      ArrowDown: (current) => mod(current - step, frameCount),
      Home: () => 0,
      End: () => frameCount - 1,
    };
    const onKeyDown = (e: KeyboardEvent) => {
      const target = keyTargets[e.key];
      if (target === undefined) return;
      e.preventDefault();
      const newFrame = target(currentFrameRef.current);
      currentFrameRef.current = newFrame;
      drawFrame(newFrame);
      setFrame(newFrame);
    };

    const observer = new ResizeObserver(() => {
      drawFrame(currentFrameRef.current);
    });

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointercancel", onPointerUp);
    canvas.addEventListener("keydown", onKeyDown);
    observer.observe(canvas.parentElement || canvas);
    drawFrame(currentFrameRef.current);

    return () => {
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerUp);
      canvas.removeEventListener("keydown", onKeyDown);
      observer.disconnect();
    };
  }, [canvasRef, drawFrame, enabled, frames.length, isLoaded, sensitivity]);

  return { frame, loadProgress, isLoaded, isDragging, canvasReady };
}
